/**
 * Helpers around the Zoho CRM Embedded App SDK (`window.ZOHO`):
 * org variables, data-center detection and module/field metadata.
 */

import { ORG_VARS, orgVarName } from "./orgVariables";

const ZOHO = window.ZOHO;

// all Zoho data centers except China
const DATA_CENTER_MAP = {
  US: "https://www.zohoapis.com",
  EU: "https://www.zohoapis.eu",
  AU: "https://www.zohoapis.com.au",
  IN: "https://www.zohoapis.in",
  JP: "https://www.zohoapis.jp",
  SA: "https://www.zohoapis.sa",
  CA: "https://www.zohoapis.ca",
};

const SANDBOX_DATA_CENTER_URL = "https://plugin-easymysqlsync.zohosandbox.com";

/** Region code ("US", "EU", ...) for a data-center URL; used for display. */
export const dataCenterRegionFromUrl = (url) => {
  if (url === SANDBOX_DATA_CENTER_URL) return "Sandbox";
  return (
    Object.keys(DATA_CENTER_MAP).find(
      (region) => DATA_CENTER_MAP[region] === url
    ) || "—"
  );
};

/** Data-center base URL for the org this widget is embedded in. */
export const detectDataCenterUrl = async () => {
  const currentEnv = await ZOHO.CRM.CONFIG.GetCurrentEnvironment();
  if (process.env.REACT_APP_SANDBOX === "true") {
    return SANDBOX_DATA_CENTER_URL;
  }
  return DATA_CENTER_MAP?.[currentEnv?.deployment] || "";
};

/* ------------------------------------------------------------------ */
/* Org variables                                                       */
/* ------------------------------------------------------------------ */

/** Fetch several org variables; returns the `Content` map keyed by full name. */
export const fetchOrgVariablesMap = async (suffixes) => {
  const resp = await ZOHO.CRM.API.getOrgVariable({
    apiKeys: suffixes.map(orgVarName),
  });
  return resp?.Success?.Content;
};

/** Fetch a single org variable; Zoho returns the raw value in that case. */
export const fetchSingleOrgVariable = async (suffix) => {
  const resp = await ZOHO.CRM.API.getOrgVariable({
    apiKeys: [orgVarName(suffix)],
  });
  return resp?.Success?.Content;
};

export const getOrgVarValue = (content, suffix) =>
  content?.[orgVarName(suffix)]?.value;

/** Like getOrgVarValue but treats missing / literal "null" values as absent. */
export const resolveOrgVarValue = (content, suffix, fallback) => {
  const value = getOrgVarValue(content, suffix);
  return value && value !== "null" ? value : fallback;
};

export const setOrgVariable = (suffix, value) =>
  ZOHO.CRM.CONNECTOR.invokeAPI("crm.set", {
    apiname: orgVarName(suffix),
    value,
  });

/**
 * Fetch the standard set of extension org variables in one call.
 * Returns { apiKey, orgId, zapiKey, dataCenterUrl, dbConnectionUrl, raw }.
 */
export const fetchExtensionSettings = async ({
  includeDbUrl = false,
  dataCenterFallback = "",
} = {}) => {
  const suffixes = [
    ORG_VARS.API_KEY,
    ORG_VARS.ORGANIZATION_ID,
    ORG_VARS.ZAPI_KEY,
    ORG_VARS.DATA_CENTER_URL,
  ];
  if (includeDbUrl) suffixes.push(ORG_VARS.DB_CONNECTION_URL);

  const content = await fetchOrgVariablesMap(suffixes);

  return {
    apiKey: getOrgVarValue(content, ORG_VARS.API_KEY),
    orgId: getOrgVarValue(content, ORG_VARS.ORGANIZATION_ID),
    zapiKey: getOrgVarValue(content, ORG_VARS.ZAPI_KEY),
    dataCenterUrl: resolveOrgVarValue(
      content,
      ORG_VARS.DATA_CENTER_URL,
      dataCenterFallback
    ),
    dbConnectionUrl: includeDbUrl
      ? getOrgVarValue(content, ORG_VARS.DB_CONNECTION_URL)
      : undefined,
    raw: content,
  };
};

/* ------------------------------------------------------------------ */
/* Module / field metadata                                             */
/* ------------------------------------------------------------------ */

/** Modules that the extension can sync (visible, editable, top-level, searchable). */
export const fetchSyncableModules = async () => {
  const resp = await ZOHO.CRM.META.getModules();
  return (resp.modules || []).filter(
    (module) =>
      module.visible &&
      module.editable &&
      !module.parent_module?.id &&
      module.global_search_supported &&
      module.generated_type !== "linking"
  );
};

/** Module fields minus the types the sync cannot store. */
export const fetchFilteredModuleFields = async (moduleApiName) => {
  const resp = await ZOHO.CRM.META.getFields({ Entity: moduleApiName });
  return (resp.fields || []).filter(
    (field) =>
      field.data_type !== "subform" && field.data_type !== "multiselectlookup"
  );
};

export const toFieldSummary = (field) => ({
  api_name: field.api_name,
  field_label: field.field_label,
  data_type: field.data_type,
});

/** Same as fetchFilteredModuleFields but summarized and error-wrapped. */
export const fetchModuleFieldSummaries = async (moduleApiName) => {
  try {
    const fields = await fetchFilteredModuleFields(moduleApiName);
    return { error: null, data: fields.map(toFieldSummary) };
  } catch (err) {
    return { data: null, error: err };
  }
};
